import * as R from 'ramda';
import * as RuleUtils from '../screens/ScreenJourneys/utils/RuleUtils';
import QueryBuilder from '../screens/ScreenJourneys/QueryBuilder';

import { AMP_KEY, WEB_KEY } from '../screens/ScreenJourneys/utils/journeyConstants';
import { LOADING_STATUS } from '../support/constants';

import {
  FETCH_APP_STATS,
  FETCH_RULES,
  FETCH_VIEWS,
  SAVE_RULE,
  SAVE_VIEW,
  DELETE_RULE,
  DELETE_VIEW,
  UPDATE_SCRATCHPAD_RULE,
  UPDATE_SCRATCHPAD_VIEW,
  SELECT_RULE_FOR_EDIT,
  SELECT_VIEW_FOR_EDIT,
  ADD_VIEW_FOR_RULE,
  REMOVE_VIEW_FOR_RULE,
  SET_VIEW_FREQUENCY,
  REORDER_TABLE,
  SAVE_REORDERED_RULES,
  PRIORITY_SORT,
  ALL_VIEWS_DICTIONARY,
  GET_VIEWS_AVAILABLE_FOR_PLATFORM,
  JOURNEY_DISMISS_SAVE_WARNING,
  SEARCH_GEO_REGIONS,
  LOAD_RULE_GEO_REGIONS,
  CLEAR_RULE_FOR_EDIT,
  UPDATE_SCRATCHPAD_RULE_BUILDER_ADD_BY_PATH,
  UPDATE_SCRATCHPAD_RULE_BUILDER_REMOVE_BY_PATH,
  UPDATE_SCRATCHPAD_RULE_TYPE,
  UPDATE_CREATIVES_PAGINATION_CURRENT_PAGE,
  UPDATE_CREATIVES_PAGINATION_PAGE_DATA,
  UPDATE_CREATIVES_PAGINATION_FILTERED_VIEWS,
  UPDATE_CREATIVES_FILTERS_DEVICE_TYPE,
  UPDATE_CREATIVES_FILTERS_CREATIVE_TYPE,
  UPDATE_CREATIVES_FILTERS_JOURNEY_OPTION,
  UPDATE_CREATIVES_FILTERS_AUTHOR,
  RESET_CREATIVES_PAGINATION,
  NULLIFY_CREATIVES_FILTERS,
  UPDATE_SCRATCHPAD_VIEW_QR_CODE_SETTINGS,
  UPDATE_SCRATCHPAD_VIEW_QR_CODE_PREVIEW_URL,
  GET_LANGUAGE_TABLE,
  SAVE_LANGUAGE_TABLE,
  JOURNEYS_LOADING_STATUS_LOADED,
  JOURNEYS_LOADING_STATUS_LOADING,
  RESTORE_SCRATCHPAD_VIEW_QR_CODE_SETTINGS,
  SYNC_SCRATCHPAD_VIEW_QR_CODE_SETTINGS,
  SET_BRANCH_VIEW_FOR_RULE,
  BRANCH_VIEW_LOADED,
} from '../actions/JourneyActions';

import { ReduxAsyncActionsService } from '../../redux/services/ReduxAsyncActions.service';
import { JourneyReducerService } from '../services/JourneyReducerService/JourneyReducerService';

import type { JourneyReducer as JourneyReducerType, Rule } from '../types/journeyTypes';

export const INIT_STATE: JourneyReducerType = {
  version: 0,
  rules: [],
  views: [],
  dictionaryRules: new Map(),
  dictionaryViews: new Map(),
  editing_view_id: '',
  editing_rule_id: '',
  scratchpad_view: null,
  scratchpad_rule: null,
  warningModalDismiss: {},
  rulesLoadingStatus: LOADING_STATUS.INIT,
  viewsLoadingStatus: LOADING_STATUS.INIT,
  loadingStatus: LOADING_STATUS.INIT,
  ruleRegionMap: {},
  searchResultRegions: [],
  enablePrioritySave: false,
  creativesPaginationObject: {
    itemsPerPage: 8,
    currentPage: 1,
    pageData: [],
    filteredViews: [],
  },
  creativesFilters: {
    author: undefined,
    deviceType: undefined,
    creativeType: undefined,
    journeyOption: undefined,
  },
};

const ARCHIVED_PRIORITY = 999;

const Handler = (state: JourneyReducerType, action) => {
  switch (action.type) {
    case SET_VIEW_FREQUENCY: {
      const { rule_id, view_index, freq } = action.data;
      const currentRule = JourneyReducerService.findScratchpadOrRuleById(state, rule_id);

      if (currentRule) {
        const newFreq = Math.max(Math.min(freq, 100), 0);
        const branchViews = currentRule.branch_views || [];

        const totals = branchViews.reduce(
          (acc, view) => {
            if (acc.count === view_index) {
              view.percentage = newFreq;
            }
            acc.total += view.percentage;
            acc.count++;

            return acc;
          },
          { count: 0, total: 0 }
        );

        totals.overage = totals.total > 100 ? totals.total - 100 : 0;
        totals.count--;

        // @ts-ignore
        const extraViews = R.sortBy(R.prop('percentage'), R.remove(view_index, 1, currentRule.branch_views));

        extraViews.forEach((view: any) => {
          let target = (totals.overage / totals.count) >>> 0; // eslint-disable-line no-bitwise
          if (totals.overage % totals.count) {
            target++;
          }
          if (target === 0) {
            return;
          }

          const viewTotal = view.percentage;
          if (viewTotal < target) {
            view.percentage = 0;
            totals.overage -= viewTotal;
          } else {
            view.percentage -= target;
            totals.overage -= target;
          }
          totals.count--;
        });
      }

      return {
        ...state,
        scratchpad_rule: {
          ...state.scratchpad_rule,
          ...currentRule,
        },
      };
    }
    case REMOVE_VIEW_FOR_RULE: {
      const { rule_id, view_index } = action.data;
      const rule = JourneyReducerService.findScratchpadOrRuleById(state, rule_id);
      if (rule) {
        rule.branch_views = rule.branch_views || [];
        rule.branch_views.splice(view_index, 1);
      }

      return {
        ...state,
        scratchpad_rule: {
          ...state.scratchpad_rule,
          ...rule,
        },
      };
    }
    case ADD_VIEW_FOR_RULE: {
      const { rule_id } = action.data;
      const rule = JourneyReducerService.findScratchpadOrRuleById(state, rule_id);
      if (rule) {
        const branch_view = RuleUtils.getCleanBranchView();
        rule.branch_views = rule.branch_views || [];
        rule.branch_views.push(branch_view);
      }

      return {
        ...state,
        scratchpad_rule: {
          ...state.scratchpad_rule,
          ...rule,
        },
      };
    }
    case SET_BRANCH_VIEW_FOR_RULE: {
      const { rule_id, view_id, view_index } = action;
      const rule = JourneyReducerService.findScratchpadOrRuleById(state, rule_id);
      if (rule) {
        rule.branch_views = rule.branch_views || [];
        const branch_view = rule.branch_views[view_index] || {
          number_of_use: 100000,
          percentage: 42,
        };
        branch_view.branch_view_id = view_id;
        rule.branch_views[view_index] = branch_view;
      }

      return {
        ...state,
        scratchpad_rule: rule,
      };
    }
    case BRANCH_VIEW_LOADED: {
      const { branch_view } = action;
      const branchView = JourneyReducerService.parseStringViewDataToObject(branch_view);

      const dictionaryViews = state.dictionaryViews;
      dictionaryViews.set(branchView.id, branchView);
      const newViews = JourneyReducerService.mapDictionaryToArray(dictionaryViews);

      let scratchpadView = state.scratchpad_view;
      if (state.editing_view_id) {
        scratchpadView = R.clone(JourneyReducerService.findViewById(state.views, state.editing_view_id));
      }

      return {
        ...state,
        loadingStatus: LOADING_STATUS.LOADED,
        scratchpad_view: scratchpadView,
        views: newViews,
        all_views_dictionary: {
          ...state.all_views_dictionary,
          [branchView.id]: branchView,
        },
      };
    }
    case SELECT_VIEW_FOR_EDIT: {
      const { view_id } = action.data;
      const editingView = JourneyReducerService.findViewById(state.views, view_id);

      return {
        ...state,
        editing_view_id: view_id,
        scratchpad_view: editingView,
      };
    }
    case SELECT_RULE_FOR_EDIT: {
      const { rule_id } = action.data;

      let rule = state.scratchpad_rule;
      let editingRuleId = state.editing_rule_id;
      let ruleRegionMap = state.ruleRegionMap;
      if (rule_id != editingRuleId || !rule) {
        if (rule_id) {
          rule = JourneyReducerService.findScratchpadOrRuleById(state, rule_id);
          rule = JourneyReducerService.addOriginalTitleToRule(rule as Rule);
          editingRuleId = rule_id;
          ruleRegionMap = {};
        } else {
          // @ts-ignore
          rule = RuleUtils.getCleanRuleObject();
          rule = JourneyReducerService.addOriginalTitleToRule(rule as Rule);
          editingRuleId = rule.id;
        }
      }

      return {
        ...state,
        scratchpad_rule: rule,
        editing_rule_id: editingRuleId,
        ruleRegionMap,
      };
    }
    case CLEAR_RULE_FOR_EDIT: {
      return {
        ...state,
        editing_rule_id: '',
        scratchpad_rule: null,
      };
    }
    case UPDATE_SCRATCHPAD_VIEW: {
      const { data } = action;
      const scratchpadView = state.scratchpad_view || JourneyReducerService.getCleanViewObject();
      if (scratchpadView) {
        scratchpadView[data.key] = data.value;
      }
      return {
        ...state,
        scratchpad_view: scratchpadView,
      };
    }
    case UPDATE_SCRATCHPAD_RULE: {
      const { data } = action;
      const scratchpadRule = state.scratchpad_rule || RuleUtils.getCleanRuleObject();
      if (scratchpadRule) {
        scratchpadRule[data.key] = data.value;
      }
      return {
        ...state,
        scratchpad_rule: {
          ...state.scratchpad_rule,
          ...scratchpadRule,
        },
      };
    }
    case UPDATE_SCRATCHPAD_RULE_BUILDER_ADD_BY_PATH: {
      const { data } = action;
      const { path, value } = data;

      let scratchpadRule = state.scratchpad_rule || RuleUtils.getCleanRuleObject();
      if (scratchpadRule) {
        scratchpadRule.builder.addDataByPath(path, value);
        scratchpadRule.query = scratchpadRule.builder.getQuery();
        scratchpadRule = scratchpadRule;
      }

      return {
        ...state,
        scratchpad_rule: scratchpadRule,
      };
    }
    case UPDATE_SCRATCHPAD_RULE_BUILDER_REMOVE_BY_PATH: {
      const { data } = action;
      const { path, value } = data;

      let scratchpadRule = state.scratchpad_rule || RuleUtils.getCleanRuleObject();
      if (scratchpadRule) {
        scratchpadRule.builder.removeDataByPath(path, value);
        scratchpadRule.query = scratchpadRule.builder.getQuery();
        scratchpadRule = scratchpadRule;
      }

      return {
        ...state,
        scratchpad_rule: scratchpadRule,
      };
    }
    case UPDATE_SCRATCHPAD_RULE_TYPE: {
      const { data } = action;
      const { type } = data;
      let scratchpadRule = state.scratchpad_rule || RuleUtils.getCleanRuleObject();
      if (scratchpadRule) {
        // @ts-ignore
        scratchpadRule.type = type;
        scratchpadRule = scratchpadRule;
      }

      return {
        ...state,
        scratchpad_rule: scratchpadRule,
      };
    }
    case FETCH_RULES.request: {
      return {
        ...state,
        rulesLoadingStatus: LOADING_STATUS.LOADING,
      };
    }
    case FETCH_RULES.response: {
      const { data: newRules } = action;

      const rules = newRules.map((rule) => ({
        ...rule,
        priority: rule.status === 'ARCHIVED' ? ARCHIVED_PRIORITY : rule.priority,
        builder: new QueryBuilder(rule.query),
        type: rule.type === 'desktop' ? 'desktop' : 'mobile',
      }));

      const dictionaryRules = JourneyReducerService.mapArrToDictionary(rules);

      let scratchpadRule = state.scratchpad_rule;
      if (state.editing_rule_id && state.scratchpad_rule?.id !== state.editing_rule_id) {
        scratchpadRule =
          dictionaryRules.get(state.editing_rule_id) ||
          JourneyReducerService.findScratchpadOrRuleById(state, state.editing_rule_id);

        scratchpadRule = JourneyReducerService.addOriginalTitleToRule(scratchpadRule as Rule);
      }

      return {
        ...state,
        rules,
        dictionaryRules,
        scratchpad_rule: scratchpadRule,
        loadingStatus: LOADING_STATUS.LOADED,
        rulesLoadingStatus: LOADING_STATUS.LOADED,
      };
    }
    case FETCH_RULES.error: {
      return {
        ...state,
        rulesLoadingStatus: LOADING_STATUS.LOADED,
      };
    }
    case FETCH_VIEWS.request: {
      return {
        ...state,
        viewsLoadingStatus: LOADING_STATUS.LOADING,
      };
    }
    case FETCH_VIEWS.response: {
      const { data: views } = action;

      const dictionaryViews = JourneyReducerService.mapArrToDictionary(views);

      let scratchpadView = state.scratchpad_view;
      if (state.editing_view_id) {
        scratchpadView = JourneyReducerService.findViewById(state.views, state.editing_view_id);
      }

      return {
        ...state,
        dictionaryViews,
        views,
        scratchpad_view: scratchpadView,
        loadingStatus: LOADING_STATUS.LOADED,
        viewsLoadingStatus: LOADING_STATUS.LOADED,
      };
    }
    case FETCH_VIEWS.error: {
      return {
        ...state,
        viewsLoadingStatus: LOADING_STATUS.LOADED,
      };
    }
    case FETCH_APP_STATS.response: {
      return {
        ...state,
        app_stats: action.data || {},
      };
    }
    case SAVE_RULE.response: {
      const {
        data: { data: actionData },
      } = action;

      const dictionaryRules = state.dictionaryRules;
      const newRule = R.assoc('builder', new QueryBuilder(actionData.query), actionData);
      dictionaryRules.set(newRule.id, newRule);
      const newRules = JourneyReducerService.mapDictionaryToArray(dictionaryRules);
      let scratchpadRule = state.scratchpad_rule;
      let editingRuleId = state.editing_rule_id;
      if (actionData) {
        scratchpadRule = JourneyReducerService.findScratchpadOrRuleById(state, newRule.id);
        editingRuleId = actionData.id;
      }
      return {
        ...state,
        dictionaryRules,
        rules: newRules,
        scratchpad_rule: {
          ...state.scratchpad_rule,
          ...scratchpadRule,
        },
        editing_rule_id: editingRuleId,
        loadingStatus: LOADING_STATUS.LOADED,
      };
    }
    case JOURNEYS_LOADING_STATUS_LOADING: {
      return {
        ...state,
        loadingStatus: LOADING_STATUS.LOADING,
      };
    }
    case JOURNEYS_LOADING_STATUS_LOADED: {
      return {
        ...state,
        loadingStatus: LOADING_STATUS.LOADED,
      };
    }
    case SAVE_VIEW.request: {
      return {
        ...state,
        loadingStatus: LOADING_STATUS.LOADING,
      };
    }
    case SAVE_VIEW.response: {
      const { data: view } = action;
      const dictionaryViews = state.dictionaryViews;

      dictionaryViews.set(view.id, view);
      const views = JourneyReducerService.mapDictionaryToArray(dictionaryViews);

      const editingViewId = state.editing_view_id;
      let scratchpadView = state.scratchpad_view;
      if (editingViewId) {
        scratchpadView = JourneyReducerService.findViewById(state.views, state.editing_view_id);
      }

      return {
        ...state,
        loadingStatus: LOADING_STATUS.LOADED,
        views,
        dictionaryViews,
        scratchpad_view: scratchpadView,
      };
    }
    case DELETE_RULE.response: {
      const { data: ruleId } = action;

      const dictionaryRules = state.dictionaryRules;
      dictionaryRules.delete(ruleId);

      const rules = JourneyReducerService.mapDictionaryToArray(dictionaryRules);

      return {
        ...state,
        rules,
        dictionaryRules,
      };
    }
    case DELETE_VIEW.response: {
      const { data: viewId } = action;

      const dictionaryViews = state.dictionaryViews;

      dictionaryViews.delete(viewId);

      const views = JourneyReducerService.mapDictionaryToArray(dictionaryViews);

      return {
        ...state,
        views,
        dictionaryViews,
      };
    }
    case GET_LANGUAGE_TABLE: {
      const { data: languageTables } = action;
      return {
        ...state,
        language_tables: languageTables,
      };
    }
    case SAVE_LANGUAGE_TABLE: {
      const { data } = action;
      const languageTables = state.language_tables || [];

      state.language_tables = state.language_tables || [];
      let index = R.findIndex((t) => {
        return t.id === data.id;
      }, languageTables);
      if (index < 0) {
        index = languageTables.length;
      }
      languageTables[index] = data;

      return {
        ...state,
        language_tables: languageTables,
      };
    }
    case SAVE_REORDERED_RULES.response: {
      const clonedRules: Rule[] = state.rules;

      R.forEach((rule: Rule) => {
        if (rule.status === 'ARCHIVED') {
          rule.priority = ARCHIVED_PRIORITY;
        }
        rule.builder = new QueryBuilder(rule.query);

        const index = R.findIndex(R.propEq('id', rule.id), clonedRules);

        clonedRules[index] = rule;
      }, action.data);

      const dictionaryRules = JourneyReducerService.mapArrToDictionary(clonedRules);

      return {
        ...state,
        rules: clonedRules,
        dictionaryRules,
        enablePrioritySave: false,
      };
    }
    case REORDER_TABLE: {
      const { fromId, toId } = action.data;
      const clonedRules: Rule[] = Object.assign([], state.rules);
      const fromIndex = clonedRules.findIndex((rule) => rule.id == fromId);
      const toIndex = clonedRules.findIndex((rule) => rule.id == toId);

      clonedRules.splice(toIndex, 0, clonedRules.splice(fromIndex, 1)[0]);

      const dictionaryRules = JourneyReducerService.mapArrToDictionary(clonedRules);

      return {
        ...state,
        rules: clonedRules,
        dictionaryRules,
        enablePrioritySave: true,
      };
    }
    case PRIORITY_SORT: {
      let rules: Rule[] = state.rules;

      // @ts-ignore
      rules = R.sortBy(R.prop('priority'), rules);

      const dictionaryRules = JourneyReducerService.mapArrToDictionary(rules);

      return {
        ...state,
        rules,
        dictionaryRules,
        enablePrioritySave: false,
      };
    }
    case ALL_VIEWS_DICTIONARY: {
      const { payload } = action;

      const allViewsDictionary = payload.branch_views.reduce(
        (dict, view) => ({
          ...dict,
          [view.id]: view,
        }),
        {}
      );
      return {
        ...state,
        all_views_dictionary: allViewsDictionary,
      };
    }
    case GET_VIEWS_AVAILABLE_FOR_PLATFORM: {
      const { templateTypeOptions, platform } = action.payload;
      const reqPlatform = platform.indexOf(AMP_KEY) !== -1 ? AMP_KEY : WEB_KEY;

      return {
        ...state,
        templateTypeOptions: [...templateTypeOptions.filter((type) => type.availableFor[reqPlatform])],
      };
    }
    case JOURNEY_DISMISS_SAVE_WARNING: {
      const ruleId = action.data;
      const warningModalDismiss = {
        ...state.warningModalDismiss,
        [ruleId]: true,
      };
      return {
        ...state,
        warningModalDismiss,
      };
    }
    case SEARCH_GEO_REGIONS.response: {
      const ruleRegionMap = state.ruleRegionMap;
      const regions = action.data.map((region) => {
        region.label = QueryBuilder.getRegionLabel(region);
        region.value = QueryBuilder.getRegionKey(region);
        // Add any new entries to the internal lookup map
        ruleRegionMap[region.value] = region;
        return region;
      });
      return {
        ...state,
        ruleRegionMap,
        searchResultRegions: regions,
      };
    }
    case LOAD_RULE_GEO_REGIONS.response: {
      const regionMap = action.data.reduce((acc, region) => {
        var key = region ? QueryBuilder.getRegionKey(region) : null;
        if (key) {
          acc[key] = region;
        }
        return acc;
      }, {});
      const rule: Rule = state.scratchpad_rule as Rule;
      // @ts-ignore
      rule.builder.setData(Object.assign({}, rule.builder.getData(), { regions: action.data }));

      return {
        ...state,
        scratchpad_rule: rule,
        ruleRegionMap: regionMap,
        loadingRegions: false,
      };
    }
    case UPDATE_CREATIVES_PAGINATION_CURRENT_PAGE: {
      const { data } = action;
      return {
        ...state,
        creativesPaginationObject: {
          ...state.creativesPaginationObject,
          currentPage: data,
        },
      };
    }
    case UPDATE_CREATIVES_PAGINATION_PAGE_DATA: {
      const { data } = action;
      return {
        ...state,
        creativesPaginationObject: {
          ...state.creativesPaginationObject,
          pageData: data,
        },
      };
    }
    case UPDATE_CREATIVES_PAGINATION_FILTERED_VIEWS: {
      const { data } = action;
      return {
        ...state,
        creativesPaginationObject: {
          ...state.creativesPaginationObject,
          filteredViews: data,
        },
      };
    }
    case RESET_CREATIVES_PAGINATION: {
      return {
        ...state,
        creativesPaginationObject: {
          ...state.creativesPaginationObject,
          currentPage: 1,
          itemsPerPage: 8,
        },
      };
    }
    case UPDATE_CREATIVES_FILTERS_AUTHOR: {
      const { data } = action;
      return {
        ...state,
        creativesFilters: {
          ...state.creativesFilters,
          author: data,
        },
      };
    }
    case UPDATE_CREATIVES_FILTERS_DEVICE_TYPE: {
      const { data } = action;
      return {
        ...state,
        creativesFilters: {
          ...state.creativesFilters,
          deviceType: data,
        },
      };
    }
    case UPDATE_CREATIVES_FILTERS_CREATIVE_TYPE: {
      const { data } = action;
      return {
        ...state,
        creativesFilters: {
          ...state.creativesFilters,
          creativeType: data,
        },
      };
    }
    case UPDATE_CREATIVES_FILTERS_JOURNEY_OPTION: {
      const { data } = action;
      return {
        ...state,
        creativesFilters: {
          ...state.creativesFilters,
          journeyOption: data,
        },
      };
    }
    case NULLIFY_CREATIVES_FILTERS: {
      return {
        ...state,
        creativesFilters: {
          ...state.creativesFilters,
          author: undefined,
          deviceType: undefined,
          creativeType: undefined,
          journeyOption: undefined,
        },
      };
    }
    case UPDATE_SCRATCHPAD_VIEW_QR_CODE_SETTINGS: {
      const { data } = action;
      const scratchpadView = state.scratchpad_view;
      if (scratchpadView) {
        scratchpadView.data.qrCodeSettings = data;
      }
      return {
        ...state,
        scratchpad_view: scratchpadView,
      };
    }
    case RESTORE_SCRATCHPAD_VIEW_QR_CODE_SETTINGS: {
      const scratchpadView = state.scratchpad_view;
      if (scratchpadView) {
        scratchpadView.data.qrCodeSettings = scratchpadView.prevQrCodeSettings;
      }
      return {
        ...state,
        scratchpad_view: scratchpadView,
      };
    }
    case SYNC_SCRATCHPAD_VIEW_QR_CODE_SETTINGS: {
      const scratchpadView = state.scratchpad_view;
      if (scratchpadView) {
        scratchpadView.prevQrCodeSettings = scratchpadView.data.qrCodeSettings;
      }
      return {
        ...state,
        scratchpad_view: scratchpadView,
      };
    }
    case UPDATE_SCRATCHPAD_VIEW_QR_CODE_PREVIEW_URL: {
      const { data } = action;

      const scratchpadView = state.scratchpad_view;
      if (scratchpadView) {
        scratchpadView.qrCodePreviewUrl = data;
      }
      return {
        ...state,
        scratchpad_view: scratchpadView,
      };
    }
  }

  return state;
};

const JourneyReducer = ReduxAsyncActionsService.wrapReducer(INIT_STATE, Handler as any, {});

export default JourneyReducer;
